import {
  ChangeEvent,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  Await,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation
} from "react-router-dom";
import { useProductFruitsApi } from "react-product-fruits";

import { CANCELLATION_SURVEY_ID } from "providers/ProductFruitsProvider/constants";

import { ReactComponent as XIcon } from "assets/icons/x-icon.svg";

import { cx, fmtDate, fmtNumber } from "utils";

import { useFeatureSwitch } from "components/FeatureSwitch";
import Button from "components/Button";
import SubmitButton from "components/SubmitButton";
import TaggedText from "components/TaggedText";
import Toaster, { ToasterId } from "components/Toaster";

import OldUpgradeSubscriptionModal from "v2/components/billing/modals/OldUpgradeSubscriptionModal";
import {
  CheckingUnpaidInvoicesModalContent,
  DisabledBecauseOfUnpaidInvoicesModalContent
} from "v2/components/billing/modals/CancelSubscription";
import { ErrorMessage } from "components/Modal/ErrorMessage";
import { IOnboardingQuestion } from "components/onboarding/OnboardingPage/Pages";
import FormWithValidation from "components/FormWithValidation";
import QuestionsPage from "components/onboarding/OnboardingPage/QuestionsPage";

import useFormattedActionError from "hooks/useFormattedActionError";
import { useDarkModeForNewDesign } from "hooks/useDarkModeForNewDesign";

import { useUser } from "routes/dataroutes/UserData";

import { path as HomePath } from "v2/pages/home/HomeRoute";

import ChangeSubscriptionModal from "./ChangeSubscriptionModal";
import { RouteErrors } from "routes/data-routes/types";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { ActiveSubscriptionFormActions } from "routes/data-routes/billing-data/active-subscription/ActiveSubscriptionFormActions";
import {
  ActiveSubscriptionDataRoutePath
} from "routes/data-routes/billing-data/active-subscription/route.meta";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";

// Re-export
export { HostedScraperErrorReportModal, HostedScraperCancelJobModal, HostedScraperJobInfoModal } from 'components/Modal/HostedScraperModals';
export { default as V2DeleteAccountModal } from "v2/pages/billing/manage-plan/DeleteAccountModal";


type ModalChildren = ReactNode | ((renderProps: { closeModal: () => void }) => ReactNode);

interface IModalProps {
  headline: string;
  wrapHeadline?: boolean;
  children: ModalChildren;
  closable?: boolean;
  closeOnEscapeKey?: boolean;
  /**
   * Returning boolean `true` prevents the modal to perform its default closing behavior to navigate back in history
   * or to a destination page. Can be useful if the modal is rendered without navigating to a different URL.
   */
  onClose?: () => boolean | void;
  preventClosingIf?: boolean;
  goBackInHistoryOnClose?: boolean;
  goToOnClose?: string;
}

export default function Modal(
  {
    headline,
    wrapHeadline = true,
    children,
    closable = true,
    closeOnEscapeKey = true,
    ...closeOpts
  }: IModalProps
) {
  const navigate = useNavigate();
  const location = useLocation();
  const useDarkMode = useDarkModeForNewDesign();

  const closeModal = useCallback(() => {
    if (closeOpts?.preventClosingIf) {
      return;
    }

    if (closeOpts?.onClose?.()) {
      return;
    }

    if (closeOpts?.goBackInHistoryOnClose) {
      navigate(-1);
    } else {
      const prevPath = closeOpts?.goToOnClose || location?.state?.backgroundLocation || HomePath;
      navigate(prevPath, { viewTransition: true });
    }
  }, [ closeOpts, navigate, location ]);

  const closeOnEsc = useCallback((e: KeyboardEvent) => {
    if ((e.key === "Escape") && closeOnEscapeKey) {
      closeModal();
    }
  }, [ closeModal, closeOnEscapeKey ]);

  useEffect(() => {
    if (closeOnEscapeKey) {
      document.addEventListener("keydown", closeOnEsc);
      return () => document.removeEventListener("keydown", closeOnEsc);
    }
  }, [ closeOnEscapeKey, closeOnEsc ]);

  // workaround for the 'There are no focusable elements inside the <FocusTrap />' error
  let focusRef = useRef(null);

  // TODO might need to customize the viewTransition animation here
  //  by default it's a simple cross-fade effect, but originally modals were also using a bit of y-translate
  return (
    // have to "disable" the onClose callback, so dialogs won't close on outside clicks
    <Dialog as="div" className="relative z-30" open onClose={ () => {} } initialFocus={focusRef}>
      <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-neutral-500 bg-opacity-75 pointer-events-none backdrop-blur-sm" />

      <div className="fixed inset-0 z-30 overflow-y-auto" ref={focusRef} >
        <div className="flex items-end justify-center min-h-full p-4 sm:items-center sm:p-0">
          <DialogPanel className={ cx(useDarkMode && "dark", "relative w-full overflow-hidden transition-all transform bg-white sm:min-w-[500px] sm:max-w-min dark:rounded") }>
            <div>
              <div className="flex items-center justify-between px-5 dark:pt-5 dark:pb-2 py-4 border-b border-b-borderColor dark:border-b-0">
                <DialogTitle
                  as="span"
                  className={ cx(
                    "text-lg dark:text-base dark:font-medium leading-6 text-brandDarkest dark:text-primary-800",
                    !wrapHeadline && "text-nowrap",
                    (!wrapHeadline && !!closeModal) && "mr-11 dark:mr-9"
                  ) }
                >
                  {headline}
                </DialogTitle>

                { closable && (
                  <XIcon
                    className="absolute right-4 top-4 dark:right-5 dark:top-5 size-8 dark:size-6 p-1.5 transition-colors rounded-sm cursor-pointer text-gray dark:text-neutral-500 hover:text-gray-800 dark:hover:text-neutral-800 hover:bg-slate-100"
                    onClick={ closeModal }
                  />
                )}
              </div>
              { typeof children === "function" ? children({ closeModal }) : children }
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export function ModalContent({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-col p-5 gap-y-12">
      {children}
    </div>
  );
}

export function ModalSections({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-y-4 text-gray dark:text-neutral-600">
      {children}
    </div>
  );
}

export function ModalButtonsSection({ children }: { children?: ReactNode }) {
  return (
    <div className="flex justify-end gap-x-4 items-center">
      {children}
    </div>
  );
}

export function UpgradeModal({ isRedesign }: { isRedesign?: boolean } = {}) {
  // v2 billing route always uses the redesigned modal
  if (isRedesign) {
    return <ChangeSubscriptionModal />;
  }

  return <OldUpgradeSubscriptionModal />;
}


export namespace CancelSubscription {

  function CancelSubscriptionModal(modalProps: Omit<IModalProps, "headline">) {
    return (
      <Modal { ...modalProps } headline="Cancel Subscription" />
    );
  }

  export function CancelModal({ isRedesign }: { isRedesign?: boolean } = {}) {
    const user = useUser();
    const notPaidInvoices = useLoaderData() as { notPaidInvoicesPromise: Promise<any> };
    const actionData = useActionData() as RouteErrors.RouterActionError | undefined; // TODO this cast is too pessimistic, actionData can be a result of a successful action
    const navigation = useNavigation();
    const location = useLocation();
    const navigate = useNavigate();
    const { activeSubscriptionFetcher, activeSubscriptionError } = useActiveSubscription();
    const errorMessage = useFormattedActionError(activeSubscriptionError ?? actionData);

    // TODO would be way better to submit the redirection link and redirect from the action
    useCallbackAfterSubmission(
      () => {
        const surveyPath = location.pathname.replace(/\/cancel$/, "/cancellation-survey");
        navigate(surveyPath);
      },
      activeSubscriptionFetcher
    );

    return (
      <CancelSubscriptionModal preventClosingIf={ navigation.state !== "idle" } goBackInHistoryOnClose={ isRedesign }>
        { ({ closeModal }) =>
          <Suspense fallback={ <CheckingUnpaidInvoicesModalContent /> }>
            <Await
              errorElement={ <DisabledBecauseOfUnpaidInvoicesModalContent closeModalCallback={ closeModal } /> }
              resolve={ notPaidInvoices.notPaidInvoicesPromise }
            >
              <activeSubscriptionFetcher.Form
                method="POST"
                action={ ActiveSubscriptionDataRoutePath }
              >
                <ModalContent>
                  <ModalSections>
                    <div>Are you sure you want to cancel your subscription?</div>
                    <div>
                      Your subscription will remain with your current plan until the end
                      of the billing period.{ " " }
                      { user?.isBannedFromFree && (
                        <>After that, your subscription will be cancelled and your API key will be removed.</>
                      ) }
                      { !user?.isBannedFromFree && (
                        <>After that, you will be downgraded to the{ " " }
                          <span className="font-bold">FREE</span> plan, with{ " " }
                          <span className="font-bold">{ fmtNumber(1000) } API credits per month</span>.
                        </>
                      ) }
                    </div>
                    <ErrorMessage errorMessage={ errorMessage } closeModalCallback={ closeModal } />
                  </ModalSections>
                  <ModalButtonsSection>
                    <Button
                      text="Keep Subscription"
                      className="button button-secondary"
                      size="MD"
                      onClick={ closeModal }
                    />
                    <SubmitButton
                      text="Cancel Subscription"
                      className="button button-primary destructive"
                      size="MD"
                      fetchersToCheck={ [ activeSubscriptionFetcher ] }
                      { ...ActiveSubscriptionFormActions.cancel }
                      disabled={ (actionData as RouteErrors.RouterActionError)?.error?.meta?.shouldNotRetry }
                    />
                  </ModalButtonsSection>
                </ModalContent>
              </activeSubscriptionFetcher.Form>
            </Await>
          </Suspense>
        }
      </CancelSubscriptionModal>
    );
  }

  const cancellationSurveySecondaryQuestions: { [index: string]: string; } = {
    "Lower success rate than expected": "What was the success rate you experienced and/or for what domain?",
    "Price was too high": "Do you have any other feedback?",
    "Dissatisfied with support": "Do you have any other feedback what to improve on our side?",
    "Service was difficult to use": "What would have helped you?",
    "Experienced slow response times": "Can you tell us the domain(s) you experienced slow response time?",
    "Found alternative": "Can you tell us why you moved to another solution and which alternative you chose?",
    "Documentation was unclear": "What would have helped you?",
    "Specific feature was missing": "Can you tell us which feature you were looking for?",
    "Existing feature not working as expected": "Which feature were you not satisfied with and why?",
    "Experienced frequent downtimes": "What was the success rate you experienced and/or for what domain?",
    "Other/None of the above": "Do you have any other feedback?"
  };

  const cancellationSurveyFirstQuestion: IOnboardingQuestion = {
    id: "cancellation-reason",
    question: "Can you let us know why you decided to cancel?",
    type: "single-select",
    mandatory: true,
    answers: Object.keys(cancellationSurveySecondaryQuestions)
  };

  export function CancellationSurveyModal() {
    const showPfCancellationSurvey = useFeatureSwitch("REACT_APP_PF_CANCELLATION_SURVEY_USERS");
    const { activeSubscription } = useActiveSubscription();
    const [ questions, setQuestions ] = useState([ cancellationSurveyFirstQuestion ]);

    const onAnswerCallback = useCallback((event: ChangeEvent<HTMLFormElement>) => {
      if ((event.target.type === "radio") && event.target.value) {
        setQuestions([
          cancellationSurveyFirstQuestion,
          {
            id: "cancellation-feedback",
            question: cancellationSurveySecondaryQuestions[event.target.value],
            type: "text-input",
            mandatory: false,
            answers: [ "" ]
          }
        ])
      }
    }, []);

    const actionData = useActionData() as string | RouteErrors.RouterActionError | undefined;
    const [ answersSavedToastId, setAnswersSavedToastId ] = useState<ToasterId | undefined>();
    const navigate = useNavigate();

    useEffect(() => {
      if (actionData === "OK") {
        if (!answersSavedToastId) {
          setAnswersSavedToastId(
            Toaster.success("Subscription cancelled and answers sent!", "We are sorry to see you go. Thank you for participating in our survey.")
          );
          navigate(BillingPath);
        }
      }
    }, [ actionData, navigate, answersSavedToastId, setAnswersSavedToastId ]);

    // Trigger product fruits cancellation survey if enabled else show previous survey
    useProductFruitsApi((api) => {
      if (!showPfCancellationSurvey) return;
      try {
        api?.surveys?.startSurvey(CANCELLATION_SURVEY_ID);
        api?.surveys?.listen("survey-closed", (event: any) => {
          if (event?.surveyId === CANCELLATION_SURVEY_ID) {
            navigate(BillingPath, { replace: true });
          }
        });
      } catch {
        navigate(BillingPath, { replace: true });
      }
    }, []);

    if (showPfCancellationSurvey) {
      return null;
    }


    return (
      <CancelSubscriptionModal goToOnClose={ BillingPath }>
        <ModalContent>
          <div className="flex flex-col text-gray dark:text-neutral-600">
            <div>
              <p>We're sorry to see you go!</p>
              <p>Your subscription was cancelled and will end on { fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000)) }.</p>
            </div>

            <FormWithValidation
              method="POST"
              onChange={ onAnswerCallback }
            >
              <QuestionsPage page={{ questions: questions }} questionsClassName="font-semibold" showQuestionsHint={ false } />

              <div className="flex flex-row justify-end pt-8">
                <SubmitButton
                  text="send"
                  checkFormValidity
                />
              </div>
            </FormWithValidation>

          </div>
        </ModalContent>
      </CancelSubscriptionModal>
    );
  }

}

export function CancelEnterpriseModal() {
  const { activeSubscription } = useActiveSubscription();

  return (
    <Modal headline="Cancel Subscription">
      { ({ closeModal }) =>
        <ModalContent>
          <ModalSections>
            <TaggedText
              message={ (activeSubscription?.planName || "Enterprise") + " subscriptions can not be cancelled manually. Please reach out to our [support team|contact_support] or " +
                "send an email to [support@scraperapi.com|contact_support_email]." }
              tagCallbacks={ { contact_support: closeModal } }
            />
          </ModalSections>
          <ModalButtonsSection>
            <Button text="Ok" className="button button-primary" size="MD" onClick={ closeModal }/>
          </ModalButtonsSection>
        </ModalContent>
      }
    </Modal>
  );
}
